import { apiFetch } from '@/api/request'
import { parseDownloadFilename } from '@/utils/parseDownloadFilename'
import type {
  ApiResponse,
  ConsultBoardListResponse,
  ConsultBoardPagination,
  ConsultBoardPostDetail,
  ConsultBoardPostMeta,
  ConsultBoardResponseItem,
  ConsultBoardSearchParams,
  MyConsultListItem,
  AuthorIdCheckResult,
} from '@/types/consultBoard'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/consult-board`

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드(3002)와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

export async function checkConsultAuthorId(authorId: string) {
  const query = new URLSearchParams({ authorId })
  const response = await apiFetch(`${BASE}/author-id/check?${query.toString()}`)
  const result = await parseJson<AuthorIdCheckResult>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || 'ID 확인에 실패했습니다.')
  }
  return result.data
}

export async function fetchConsultPosts(params: ConsultBoardSearchParams) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.set(key, String(value))
    }
  })

  const response = await apiFetch(`${BASE}/posts?${query.toString()}`)
  const result = await parseJson<ConsultBoardListResponse>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '목록을 불러오지 못했습니다.')
  }
  return result.data
}

export async function fetchMyConsultPosts(
  params: { page?: number; limit?: number; channel?: 'PORTAL' | 'MALL' } = {},
) {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.limit) query.set('limit', String(params.limit))
  if (params.channel) query.set('channel', params.channel)

  const response = await apiFetch(`${BASE}/posts/mine?${query.toString()}`, {
    credentials: 'include',
  })
  const result = await parseJson<{ items: MyConsultListItem[]; pagination: ConsultBoardPagination }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '목록을 불러오지 못했습니다.')
  }
  return result.data
}

export async function createConsultPost(formData: FormData) {
  const response = await apiFetch(`${BASE}/posts`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })
  const result = await parseJson<{ id: number; authorId: string }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '등록에 실패했습니다.')
  }
  return result.data
}

export async function fetchConsultPostMeta(id: number) {
  const response = await apiFetch(`${BASE}/posts/${id}`)
  const result = await parseJson<ConsultBoardPostMeta>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '글 정보를 불러오지 못했습니다.')
  }
  return result.data
}

/* 로그인 회원이 본인 글을 열 때는 password 없이 호출한다 (백엔드가 세션으로 판별) */
export async function deleteConsultPost(id: number) {
  const response = await apiFetch(`${BASE}/posts/${id}/delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '글 삭제에 실패했습니다.')
  }
}

export async function verifyConsultPost(id: number) {
  const response = await apiFetch(`${BASE}/posts/${id}/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ accessToken: string; post: ConsultBoardPostDetail; responses: ConsultBoardResponseItem[] }>(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || '본인 상담만 확인할 수 있습니다.')
  }
  return result.data
}

export async function downloadConsultResponseExcel(
  postId: number,
  responseId: number,
  accessToken: string,
  fallbackName: string,
) {
  const response = await apiFetch(`${BASE}/posts/${postId}/responses/${responseId}/excel`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const result = (await response.json()) as ApiResponse
    throw new Error(result.message || '파일 다운로드에 실패했습니다.')
  }

  const blob = await response.blob()
  const filename = parseDownloadFilename(
    response.headers.get('Content-Disposition'),
    fallbackName,
  )

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function downloadConsultExcel(id: number, accessToken: string) {
  const response = await apiFetch(`${BASE}/posts/${id}/excel`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const result = (await response.json()) as ApiResponse
    throw new Error(result.message || '파일 다운로드에 실패했습니다.')
  }

  const blob = await response.blob()
  const filename = parseDownloadFilename(
    response.headers.get('Content-Disposition'),
    `consult-${id}.xlsx`,
  )

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
